import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ThreadReply {
  id: string;
  message: string;
  sender_id: string;
  created_at: string;
  parent_id: string;
  sender?: {
    id: string;
    full_name: string | null;
  };
}

export const useThreadReplies = (
  parentId: string | undefined,
  messageType: 'channel' | 'direct'
) => {
  const queryClient = useQueryClient();
  const tableName = messageType === 'channel' ? 'channel_messages' : 'direct_messages';

  const { data: replies = [], isLoading } = useQuery({
    queryKey: ["thread-replies", parentId, messageType],
    queryFn: async () => {
      if (!parentId) return [];

      const { data, error } = await supabase
        .from(tableName)
        .select('id, message, sender_id, created_at, parent_id')
        .eq('parent_id', parentId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch sender profiles
      const senderIds = [...new Set(data?.map(r => r.sender_id) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', senderIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      return data?.map(r => ({
        ...r,
        sender: profileMap.get(r.sender_id),
      })) as ThreadReply[];
    },
    enabled: !!parentId,
  });

  const { mutateAsync: sendReply, isPending: isSending } = useMutation({
    mutationFn: async ({ 
      message, 
      channelId, 
      recipientId 
    }: { 
      message: string; 
      channelId?: string; 
      recipientId?: string;
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user || !parentId) throw new Error("Not authenticated");

      if (messageType === 'channel') {
        const { data, error } = await supabase
          .from('channel_messages')
          .insert({
            channel_id: channelId!,
            sender_id: session.session.user.id,
            message,
            parent_id: parentId,
          })
          .select()
          .single();

        if (error) throw error;

        // Increment reply count on parent
        await supabase
          .from('channel_messages')
          .update({ reply_count: replies.length + 1 })
          .eq('id', parentId);

        return data;
      } else {
        const { data, error } = await supabase
          .from('direct_messages')
          .insert({
            sender_id: session.session.user.id,
            recipient_id: recipientId!,
            message,
            parent_id: parentId,
          })
          .select()
          .single();

        if (error) throw error;

        // Increment reply count on parent
        await supabase
          .from('direct_messages')
          .update({ reply_count: replies.length + 1 })
          .eq('id', parentId);

        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thread-replies", parentId, messageType] });
      // Also invalidate the main message list to update reply counts
      queryClient.invalidateQueries({ queryKey: ["channel-messages"] });
      queryClient.invalidateQueries({ queryKey: ["direct-messages"] });
    },
  });

  return {
    replies,
    isLoading,
    sendReply,
    isSending,
  };
};
